import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { LayoutData, PageContent, VideoCard } from '@/types/content'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/site/page-hero'
import { VideoLibrary } from '@/components/media/video-library'
import { getLayout, getPage } from '@/lib/server/content/site'
import { getVideos } from '@/lib/server/content/videos'
import { setPageCache } from '@/lib/server/page'

interface VideosPageProps {
    layout: LayoutData
    page: PageContent | null
    videos: Array<VideoCard>
}

const VideosPage: FC<VideosPageProps> = ({ layout, page, videos }): ReactNode => {
    return (
        <Layout layout={layout}>
            <Seo
                title={page?.seo_title ?? 'Videos'}
                description={page?.seo_description}
                ogImage={page?.og_image ?? layout.settings.og_image}
                ogImageAlt={page?.og_image_alt}
                origin={layout.origin}
                path='/videos'
            />

            <PageHero page={page} fallback={{ eyebrow: 'Moving images', heading: 'Videos' }} phase={0.72} image='/images/lake-silhouette.jpg' imageAlt='A lone figure walking into thick fog' />

            <section id='video-library' aria-label='Video library' className='shell pb-28'>
                <VideoLibrary videos={videos} />
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<VideosPageProps> = async ctx => {
    const [layout, page, videos] = await Promise.all([getLayout(ctx.req), getPage('videos'), getVideos()])

    setPageCache(ctx)

    return { props: { layout, page, videos } }
}

export default VideosPage
